import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";

const RELATIONSHIP_OPTIONS = [
    "Spouse",
    "Son",
    "Daughter",
    "Father",
    "Mother",
    "Sibling",
    "Parent",
    "Child",
    "Guardian",
    "Other",
];

const NewBeneficiary = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        relationship: "",
        notes: "",
    });

    useEffect(() => {
        initialiseCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id]);

    const initialiseCompany = async () => {
        if (!userData?.id) {
            setCompanyId(null);
            return;
        }

        setLoading(true);
        try {
            let derivedCompanyId = userData?.company_id || userData?.companyId || null;

            if (!derivedCompanyId) {
                const response = await oneKlicks.read("", "insurance_companies", {}, "created_at DESC", "", "", "0,1");
                if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
                    derivedCompanyId = response.data[0].id;
                }
            }

            setCompanyId(derivedCompanyId || null);
        } catch (error) {
            console.error("Error resolving company for beneficiary:", error);
            toast.error("Unable to determine company. Please retry later.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name.trim()) {
            toast.error("Beneficiary name is required");
            return;
        }

        if (!formData.relationship.trim()) {
            toast.error("Relationship is required");
            return;
        }

        if (!userData?.id) {
            toast.error("User information missing. Please login again.");
            return;
        }

        if (!companyId) {
            toast.error("Company information missing. Please ensure your profile has a company assigned.");
            return;
        }

        setSubmitting(true);
        try {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const payload = {
                user_id: userData.id,
                company_id: companyId,
                name: formData.name.trim(),
                relationship: formData.relationship.trim(),
                created_at: timestamp,
                updated_at: timestamp,
            };

            console.log("Creating insurance beneficiary payload:", payload);
            const response = await oneKlicks.create("", "insurance_beneficiaries", payload);
            console.log("Create beneficiary response:", response);

            if (response?.success) {
                toast.success("Beneficiary added successfully");
                navigate("/insurance/customers");
            } else {
                toast.error(response?.message || response?.error || "Failed to add beneficiary");
            }
        } catch (error) {
            console.error("Error creating beneficiary:", error);
            toast.error("Failed to create beneficiary");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "24px" }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: "840px", margin: "0 auto" }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <h2>Add Beneficiary</h2>
                            <p style={{ color: "#6c757d", marginTop: "4px" }}>
                                Link a beneficiary to assign payouts and policy coverage priorities.
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="insurance-beneficiary-form"
                                className="btn btn-primary"
                                disabled={submitting || loading}
                            >
                                {submitting ? "Saving..." : "Save Beneficiary"}
                            </button>
                        </div>
                    </div>

                    <div className="dc-dashboardboxcontent" style={{ padding: "24px" }}>
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <form id="insurance-beneficiary-form" className="dc-formtheme" onSubmit={handleSubmit}>
                                <div className="row" style={{ rowGap: "24px" }}>
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="name">Beneficiary Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                className="form-control"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Full legal name"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="relationship">Relationship</label>
                                            <input
                                                list="relationship-options"
                                                id="relationship"
                                                name="relationship"
                                                className="form-control"
                                                value={formData.relationship}
                                                onChange={handleChange}
                                                placeholder="e.g. Spouse"
                                                required
                                            />
                                            <datalist id="relationship-options">
                                                {RELATIONSHIP_OPTIONS.map((option) => (
                                                    <option key={option} value={option} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="notes">Notes (optional)</label>
                                            <textarea
                                                id="notes"
                                                name="notes"
                                                className="form-control"
                                                rows="3"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                placeholder="Coverage preferences, identification numbers, additional context"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewBeneficiary;
